import { Element, ElementType, Group, Polyline } from '@ritabygglov/core';
import { Command } from '../../commands/Command';
import { CompositeCommand } from '../../commands/CompositeCommand';
import { CreateCommand } from '../../commands/CreateCommand';
import { DeleteCommand } from '../../commands/DeleteCommand';
import { UpdateCommand } from '../../commands/UpdateCommand';
import {
  canGroupSelection,
  computeGroupBBox,
  expandGroups,
  mergeSelectionIntoGroup,
  resolveUngroupTarget,
} from '../../helpers/groupHelpers';
import { copy } from '../../helpers/utility';
import {
  SelectionCoordinates,
  SelectionMode,
  SelectionModeHelper,
  SelectionModes,
  ViewBox,
} from '../../types';
import { nudgeMove, updateElementCoords } from '../MouseEvents/helpers';
import { KeyboardAction } from './keyboardActions';

/**
 * A side effect that the thin hook layer is responsible for executing. Handlers
 * describe *what* should happen as data; the hook interprets each effect and
 * performs the actual state mutation, command execution, or DOM interaction.
 */
export type KeyboardEffect =
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'toggleSnaplines' }
  | { type: 'executeCommand'; command: Command }
  | { type: 'setSelectedElements'; ids: string[] }
  | { type: 'setSelectionMode'; mode: SelectionMode }
  | { type: 'clearSelectionCoordinates' }
  | { type: 'setSpacePressed'; value: boolean }
  | { type: 'setShiftPressed'; value: boolean }
  | { type: 'setMetaPressed'; value: boolean }
  | { type: 'nudgeViewBox'; dx: number; dy: number }
  | { type: 'stopPolyline'; removeLastPoint: boolean }
  | { type: 'startTextEditing'; element: Element }
  | { type: 'blurActiveElement' }
  | { type: 'duplicate' }
  | { type: 'copyElements'; elements: Element[] };

export interface KeyboardActionResult {
  /** Whether the originating keyboard event's default should be prevented. */
  preventDefault?: boolean;
  effects: KeyboardEffect[];
}

export interface KeyboardHandlerContext {
  appState: { elements: { [id: string]: Element } };
  selectedElements: string[];
  selectionMode: SelectionMode;
  selectionCoordinates: SelectionCoordinates;
  viewBox: ViewBox;
  currentUserId: string | undefined;
  hasSession: boolean;
}

const clearedCoordinatesEffect: KeyboardEffect = {
  type: 'clearSelectionCoordinates',
};

const resolveSelected = (
  ids: string[],
  elements: { [id: string]: Element }
): Element[] =>
  ids.map((id) => elements[id]).filter((el): el is Element => el !== undefined);

/**
 * The Group currently open for editing, or undefined when not in
 * GroupEditing mode (or the referenced group no longer exists).
 */
const getOpenGroup = (
  selectionMode: SelectionMode,
  elements: { [id: string]: Element }
): Group | undefined => {
  if (selectionMode.type !== SelectionModes.GroupEditing) return undefined;
  const { groupId } = selectionMode;
  if (!groupId) return undefined;
  const group = elements[groupId];
  return group && group.type === ElementType.Group ? group : undefined;
};

/**
 * Shared "reset to no selection" transition used by escape / confirm cancels.
 * Stops an in-progress polyline first when one is being drawn.
 */
const buildCancelResult = (
  ctx: KeyboardHandlerContext
): KeyboardActionResult => {
  const {
    selectionMode,
    selectedElements,
    selectionCoordinates,
    appState,
    hasSession,
  } = ctx;
  const effects: KeyboardEffect[] = [];

  if (SelectionModeHelper.isAddingPolyline(selectionMode)) {
    const selectedElement = selectedElements[0];
    if (selectedElement && hasSession) {
      const element = copy(appState.elements[selectedElement]) as Polyline;
      const shouldRemoveLastPoint =
        element.points.length > selectionCoordinates.nextPointIndex;
      effects.push({
        type: 'stopPolyline',
        removeLastPoint: shouldRemoveLastPoint,
      });
    }
  }

  effects.push({
    type: 'setSelectionMode',
    mode: {
      ...selectionMode,
      type: SelectionModes.None,
      elementType: undefined,
      lineType: undefined,
    },
  });
  effects.push({ type: 'setSelectedElements', ids: [] });
  effects.push(clearedCoordinatesEffect);

  return { effects };
};

/**
 * Pure translation of a {@link KeyboardAction} into the effects required to
 * fulfil it, given the current editor state. Returns no effects when the action
 * is a no-op in the current state (e.g. deleting while updating).
 */
export const handleKeyboardAction = (
  action: KeyboardAction,
  ctx: KeyboardHandlerContext
): KeyboardActionResult => {
  const { appState, selectedElements, selectionMode, viewBox, currentUserId } =
    ctx;

  switch (action.type) {
    case 'Undo':
      return { effects: [{ type: 'undo' }] };
    case 'Redo':
      return { effects: [{ type: 'redo' }] };
    case 'ToggleSnaplines':
      return { preventDefault: true, effects: [{ type: 'toggleSnaplines' }] };
    case 'GroupSelection': {
      if (!canGroupSelection(selectedElements, selectionMode)) {
        return { preventDefault: true, effects: [] };
      }
      const { newGroup, mergedGroups } = mergeSelectionIntoGroup(
        selectedElements,
        appState.elements,
        currentUserId as string,
        Object.keys(appState.elements).length + 1
      );
      const command = new CompositeCommand([
        new CreateCommand([newGroup], currentUserId as string),
        new DeleteCommand(mergedGroups, currentUserId as string),
      ]);
      return {
        preventDefault: true,
        effects: [
          { type: 'executeCommand', command },
          { type: 'setSelectedElements', ids: [newGroup.id] },
          {
            type: 'setSelectionMode',
            mode: {
              type: SelectionModes.Selected,
              elementType: ElementType.Group,
            },
          },
        ],
      };
    }
    case 'UngroupSelection': {
      const only = resolveUngroupTarget(
        selectedElements,
        appState.elements,
        selectionMode
      );
      if (!only) {
        return { preventDefault: true, effects: [] };
      }
      return {
        preventDefault: true,
        effects: [
          {
            type: 'executeCommand',
            command: new DeleteCommand([only], currentUserId as string),
          },
          { type: 'setSelectedElements', ids: only.childIds },
          {
            type: 'setSelectionMode',
            mode: {
              type: SelectionModes.Selected,
              elementType: undefined,
            },
          },
        ],
      };
    }
    case 'ExitGroupEditing': {
      const group = getOpenGroup(selectionMode, appState.elements);
      if (!group) {
        return {
          effects: [
            { type: 'setSelectedElements', ids: [] },
            { type: 'setSelectionMode', mode: { type: SelectionModes.None } },
          ],
        };
      }

      const effects: KeyboardEffect[] = [];
      // Children may have been moved or resized while the group was open;
      // persist the group's re-fitted bbox before closing it.
      const newBbox = computeGroupBBox(group, appState.elements);
      if (
        newBbox &&
        (newBbox.x !== group.x ||
          newBbox.y !== group.y ||
          newBbox.width !== group.width ||
          newBbox.height !== group.height)
      ) {
        effects.push({
          type: 'executeCommand',
          command: new UpdateCommand(
            [{ ...group, ...newBbox }],
            [group],
            currentUserId as string,
            false
          ),
        });
      }
      effects.push({ type: 'setSelectedElements', ids: [group.id] });
      effects.push({
        type: 'setSelectionMode',
        mode: {
          type: SelectionModes.Selected,
          elementType: ElementType.Group,
        },
      });
      return { effects };
    }
    case 'ExitTextEditing':
      return {
        effects: [
          { type: 'blurActiveElement' },
          {
            type: 'setSelectionMode',
            mode: { ...selectionMode, type: SelectionModes.Selected },
          },
          clearedCoordinatesEffect,
        ],
      };
    case 'ConfirmEnter': {
      const onlySelected =
        selectedElements.length === 1
          ? appState.elements[selectedElements[0]]
          : undefined;
      if (
        onlySelected &&
        onlySelected.type === ElementType.Text &&
        selectionMode.type !== SelectionModes.Add &&
        !SelectionModeHelper.isAddingPolyline(selectionMode)
      ) {
        return {
          preventDefault: true,
          effects: [{ type: 'startTextEditing', element: onlySelected }],
        };
      }
      return buildCancelResult(ctx);
    }
    case 'CancelSelection':
      return buildCancelResult(ctx);
    case 'PressSpace':
      return { effects: [{ type: 'setSpacePressed', value: true }] };
    case 'PressShift':
      return { effects: [{ type: 'setShiftPressed', value: true }] };
    case 'PressMeta':
      return { effects: [{ type: 'setMetaPressed', value: true }] };
    case 'ReleaseSpace':
      return { effects: [{ type: 'setSpacePressed', value: false }] };
    case 'ReleaseShift':
      return { effects: [{ type: 'setShiftPressed', value: false }] };
    case 'ReleaseMeta':
      return { effects: [{ type: 'setMetaPressed', value: false }] };
    case 'DeleteSelected': {
      if (selectionMode.type === SelectionModes.Updating) {
        return { effects: [] };
      }

      const openGroup = getOpenGroup(selectionMode, appState.elements);

      // Deleting children from inside an open group: the group survives with
      // a re-fitted bbox, unless too few children would be left for it to
      // mean anything.
      if (openGroup) {
        const deletedChildren = resolveSelected(
          selectedElements,
          appState.elements
        );
        const remainingChildIds = openGroup.childIds.filter(
          (id) => !selectedElements.includes(id)
        );

        if (remainingChildIds.length <= 1) {
          // Auto-ungroup: a group with 0-1 children left is meaningless.
          return {
            effects: [
              {
                type: 'executeCommand',
                command: new CompositeCommand([
                  new DeleteCommand(deletedChildren, currentUserId as string),
                  new DeleteCommand([openGroup], currentUserId as string),
                ]),
              },
              { type: 'setSelectedElements', ids: remainingChildIds },
              {
                type: 'setSelectionMode',
                mode: {
                  type: SelectionModes.Selected,
                  elementType: undefined,
                },
              },
            ],
          };
        }

        const newBbox = computeGroupBBox(
          { ...openGroup, childIds: remainingChildIds },
          appState.elements
        ) ?? {
          x: openGroup.x,
          y: openGroup.y,
          width: openGroup.width,
          height: openGroup.height,
        };
        return {
          effects: [
            {
              type: 'executeCommand',
              command: new CompositeCommand([
                new DeleteCommand(deletedChildren, currentUserId as string),
                new UpdateCommand(
                  [
                    {
                      ...openGroup,
                      childIds: remainingChildIds,
                      ...newBbox,
                    },
                  ],
                  [openGroup],
                  currentUserId as string,
                  false
                ),
              ]),
            },
            { type: 'setSelectedElements', ids: [] },
            {
              type: 'setSelectionMode',
              mode: { ...selectionMode, type: SelectionModes.GroupEditing },
            },
          ],
        };
      }

      // Any selected Group (sole selection or alongside other elements)
      // must delete along with its children — otherwise deleting a mixed
      // selection of a group + a loose element only deletes the bare Group
      // record, leaving its children behind, ungrouped.
      const elements = expandGroups(
        resolveSelected(selectedElements, appState.elements),
        appState.elements
      );
      const command = new DeleteCommand(elements, currentUserId as string);
      return {
        effects: [
          { type: 'executeCommand', command },
          { type: 'setSelectedElements', ids: [] },
          {
            type: 'setSelectionMode',
            mode: {
              ...selectionMode,
              type: SelectionModes.None,
              elementType: undefined,
              lineType: undefined,
            },
          },
        ],
      };
    }
    case 'SetAddMode':
      return {
        effects: [
          { type: 'setSelectedElements', ids: [] },
          {
            type: 'setSelectionMode',
            mode: {
              type: SelectionModes.Add,
              elementType: action.elementType,
              ...(action.lineType ? { lineType: action.lineType } : {}),
            },
          },
        ],
      };
    case 'Duplicate':
      return { preventDefault: true, effects: [{ type: 'duplicate' }] };
    case 'Copy': {
      // A Group is only a bbox — copying it without its children would put a
      // group on the clipboard whose childIds still name the originals.
      const elements = expandGroups(
        resolveSelected(selectedElements, appState.elements),
        appState.elements
      );
      return {
        preventDefault: true,
        effects:
          elements.length > 0 ? [{ type: 'copyElements', elements }] : [],
      };
    }
    case 'Nudge': {
      if (selectedElements.length === 0) {
        const { dx, dy } = nudgeMove(action.code, viewBox.scale, 20);
        return {
          preventDefault: true,
          effects: [{ type: 'nudgeViewBox', dx, dy }],
        };
      }

      const { dx, dy } = nudgeMove(action.code, 1, 1);
      const elements = expandGroups(
        resolveSelected(selectedElements, appState.elements),
        appState.elements
      );

      if (elements.length === 0) {
        return { preventDefault: true, effects: [] };
      }

      const updatedElements = elements.map((el) =>
        updateElementCoords(el, dx, dy, el)
      );
      const command = new UpdateCommand(
        updatedElements,
        elements,
        currentUserId as string
      );
      return {
        preventDefault: true,
        effects: [{ type: 'executeCommand', command }],
      };
    }
    default:
      return { effects: [] };
  }
};
